import { useMemo } from "react";
import { AttributeRenderer, useAttributeGridForm } from "src/components";
import { Divider, Stack, Typography, useTheme } from "@mui/material";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configureProductActions } from "../../configure-product-store";
import { ConfigureProductSectionLoading } from "./configure-product-section-loading";
import { useParams } from "react-router";
import { LabelsMultiSelectChip } from "src/pages/configurator/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { canItemHaveLabels } from "src/pages/configurator/configurator-utils";

const mapStateToProps = (state: RootState) => ({
  configurationState: state.pageReducer.configureProductReducer.configurationState,
  selectedProjectLineItemId: state.pageReducer.configureProductReducer.selectedProjectLineItemId
});

const ownActions = {
  updateProjectLineItemAttributeRequest: configureProductActions.updateProjectLineItemAttributeRequest,
  getGenerateConfigurationOrderRequest: configureProductActions.getGenerateConfigurationOrderRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigureProductSectionBase = (props: Props) => {
  const { configurationState, selectedProjectLineItemId, updateProjectLineItemAttributeRequest, getGenerateConfigurationOrderRequest } = props;

  const { projectId = "", configurationId = "" } = useParams<{ projectId: string; configurationId: string }>();

  const configuratorPageT = useConfiguratorPageT();

  const theme = useTheme();

  // systemProductConfigurationItems replaces projectLineItems; items use systemProductConfigurationItemId
  const selectedProjectLineItem = useMemo(() => {
    return configurationState.loading === "has-loaded"
      ? configurationState.data.systemProductConfigurationItems.find(projectLineItem => projectLineItem.systemProductConfigurationItemId === selectedProjectLineItemId)
      : undefined;
  }, [configurationState, selectedProjectLineItemId]);

  const onAttributeChanged = () => getGenerateConfigurationOrderRequest(projectId, configurationId);

  // SharedAttributesSection renders as its own panel at the configure-product page level (see
  // configure-product.tsx) — for a root item, selectedProjectLineItem.attributes IS that same shared
  // data, so this own-attributes block stays hidden for root to avoid showing it a second time here
  const hasOwnAttributeRows = selectedProjectLineItem?.parentProjectLineItemId !== null && (selectedProjectLineItem?.attributes?.length ?? 0) > 0;

  const selectedItemId = selectedProjectLineItem?.systemProductConfigurationItemId;

  const ownAttributesForm = useAttributeGridForm(
    selectedProjectLineItem?.attributes,
    ({ attributeId, value, shouldDebounce, onError }) => {
      if (!selectedItemId) return;
      updateProjectLineItemAttributeRequest({ attributeId, value, systemProductConfigurationItemId: selectedItemId }, shouldDebounce, onError);
    },
    onAttributeChanged
  );

  const disabledOwnAttributeRows = ownAttributesForm.rows.filter(formItem => !!formItem.formState.disabled.value);
  const enabledOwnAttributeRows = ownAttributesForm.rows.filter(formItem => !formItem.formState.disabled.value);

  if (configurationState.loading === "is-loading") {
    return <ConfigureProductSectionLoading />;
  }

  if (configurationState.loading === "has-loaded") {
    if (!selectedProjectLineItem) {
      return (
        <Stack gap={2} flex={1} overflow="hidden">
          <Typography px={2.4} py={2} variant="body2" color={theme.palette.customColors.darkGrey}>
            {configuratorPageT.noProductSelected}
          </Typography>
        </Stack>
      );
    }
    return (
      <Stack gap={2} flex={1} overflow="hidden">
        <Typography px={2.4} pt={2} variant="body2" color={theme.palette.customColors.darkGrey}>
          {selectedProjectLineItem?.productDescription || ""} ({selectedProjectLineItem?.productNumber || ""})
        </Typography>
        {/* pt leaves room for the first field's floating label: MUI lifts it ~9px above the input's box, which
            this scroll container would otherwise clip. It only showed once the label chip stopped always being
            the first child here. */}
        <Stack gap={2} flex={1} overflow="auto" px={2.4} pt={1.25} pb={hasOwnAttributeRows ? 2.4 : 4.5}>
          {/* Labels live at the top of a configuration only — deeper items get no field at all */}
          {canItemHaveLabels(selectedProjectLineItem) && (
            <LabelsMultiSelectChip key={selectedProjectLineItemId} projectId={projectId} entityIds={[selectedProjectLineItemId || ""]} applyOnBlur />
          )}
          {hasOwnAttributeRows && (
            <>
              {disabledOwnAttributeRows.map(formItem => (
                <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={ownAttributesForm.handleChange} />
              ))}
              {disabledOwnAttributeRows.length > 0 && enabledOwnAttributeRows.length > 0 && <Divider />}
              {enabledOwnAttributeRows.map(formItem => (
                <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={ownAttributesForm.handleChange} />
              ))}
            </>
          )}
        </Stack>
      </Stack>
    );
  }

  return <></>;
};

export const ConfigureProductSection = connect(mapStateToProps, ownActions)(ConfigureProductSectionBase);
